import {BaseComponent} from '../base/component.base.js';
import template from './template-picker.template.html';
import styles from './template-picker.styles.css';
import {t, tp} from '../../i18n/i18n.js';
import {displayName, pageUrlPath} from '../../core/page/page.core.js';
import {RenderMode} from '../../core/vocabulary.js';

export class TemplatePickerComponent extends BaseComponent {
    #templates = [];

    #loadFailed = false;

    #onLocaleChange = () => {
        if (this.#loadFailed) {
            this.loadFailed = true;
        } else if (this.#templates.length > 0) {
            this.templates = this.#templates;
        }
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        document.addEventListener('locale-change', this.#onLocaleChange);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('locale-change', this.#onLocaleChange);
    }

    /** @param {string} name */
    set clientName(name) {
        const el = this.$('#client-name');
        if (el) el.textContent = name;
    }

    /** @param {boolean} value */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        if (overlay) {
            // Inline skeleton rows feel faster than a blocking spinner for content load.
            overlay.hidden = true;
        }
        const grid = this.$('#template-grid');
        if (!grid) return;
        if (value) {
            const list = document.createElement('div');
            list.className = 'template-list';
            for (let i = 0; i < 4; i++) {
                const row = document.createElement('div');
                row.className = 'skeleton-row';
                for (const cls of ['skeleton-icon', 'skeleton-text', 'skeleton-action']) {
                    const shape = document.createElement('div');
                    shape.className = `skeleton-shape ${cls}`;
                    row.appendChild(shape);
                }
                list.appendChild(row);
            }
            grid.replaceChildren(list);
        }
    }

    /**
     * Shows that the templates could not be loaded, with a button that emits
     * `templates-request` to ask for them again. Loaded templates replace the message.
     * @param {boolean} value
     */
    set loadFailed(value) {
        this.#loadFailed = Boolean(value);
        const grid = this.$('#template-grid');
        if (!this.#loadFailed || !grid) return;
        const empty = document.createElement('empty-state');
        const title = document.createElement('span');
        title.slot = 'title';
        title.textContent = t('templatePicker.loadFailedTitle');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.slot = 'cta';
        retry.className = 'btn-retry';
        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-rotate-right';
        icon.setAttribute('aria-hidden', 'true');
        retry.append(icon, ` ${t('global.retry')}`);
        retry.addEventListener('click', () => this.emit('templates-request'));
        empty.append(title, document.createTextNode(t('global.loadFailedBody')), retry);
        grid.replaceChildren(empty);
    }

    /** @param {import('../../shell/types/user.types.js').TemplateSummary[]} templates */
    set templates(templates) {
        this.#templates = templates || [];
        this.#loadFailed = false;
        const grid = this.$('#template-grid');
        if (!grid) return;

        grid.replaceChildren();

        const visible = this.#templates.filter(tmpl => tmpl.mode === RenderMode.PAGE || tmpl.mode === RenderMode.INDEX_FRAGMENT);
        if (visible.length === 0) {
            const empty = document.createElement('empty-state');
            const title = document.createElement('span');
            title.slot = 'title';
            title.textContent = t('templatePicker.emptyTitle');
            empty.appendChild(title);
            empty.appendChild(document.createTextNode(t('templatePicker.empty')));
            grid.appendChild(empty);
            return;
        }

        const pages = visible.filter(tmpl => tmpl.mode === RenderMode.PAGE);
        const fixed = pages.filter(tmpl => tmpl.singleInstance);
        const groups = [
            {key: 'fixed', heading: 'templatePicker.sectionFixed', icon: 'fa-file-lines',
                // The homepage template always leads the fixed-pages group.
                items: [...fixed.filter(tmpl => tmpl.homepage), ...fixed.filter(tmpl => !tmpl.homepage)]},
            {key: 'repeatable', heading: 'templatePicker.sectionRepeatable', icon: 'fa-circle-plus',
                items: pages.filter(tmpl => !tmpl.singleInstance)},
            {key: 'fragments', heading: 'templatePicker.sectionFragments', icon: 'fa-puzzle-piece',
                items: visible.filter(tmpl => tmpl.mode === RenderMode.INDEX_FRAGMENT)}
        ];

        for (const group of groups) {
            if (group.items.length === 0) continue;
            const section = document.createElement('section');
            section.className = 'template-group';
            section.dataset.group = group.key;

            const heading = document.createElement('h3');
            heading.className = 'section-heading';
            const icon = document.createElement('i');
            icon.className = `fa-solid ${group.icon}`;
            icon.setAttribute('aria-hidden', 'true');
            heading.append(icon, ` ${t(group.heading)}`);

            const list = document.createElement('div');
            list.className = 'template-list';
            group.items.forEach(tmpl => list.appendChild(this.#createRow(tmpl)));

            section.append(heading, list);
            grid.appendChild(section);
        }
    }

    /** @param {import('../../shell/types/user.types.js').TemplateSummary} tmpl */
    #createRow(tmpl) {
        const isFragment = tmpl.mode === RenderMode.INDEX_FRAGMENT;
        const existing = tmpl.singleInstance ? tmpl.existingPage : null;
        const description = tmpl.description || t('templatePicker.defaultDescription');
        const status = this.#status(tmpl, existing, isFragment);

        let actionClass = 'action-create';
        let actionIcon = 'fa-plus';
        let actionLabel = t(isFragment ? 'templatePicker.actionAdd' : 'templatePicker.actionNewPage');
        if (existing) {
            actionClass = 'action-edit';
            actionIcon = 'fa-pen-to-square';
            actionLabel = t('templatePicker.actionEdit');
        } else if (tmpl.singleInstance) {
            actionLabel = t('templatePicker.actionCreate');
        }

        const row = document.createElement('button');
        row.type = 'button';
        row.className = `template-row${existing ? ' is-existing' : ''}`;
        row.innerHTML = `
            <span class="row-icon" aria-hidden="true"><i class="fa-solid ${isFragment ? 'fa-puzzle-piece' : 'fa-file-lines'}"></i></span>
            <span class="row-main">
                <span class="row-name">${this.#esc(displayName(tmpl.name))}</span>
                <span class="row-desc">${this.#esc(description)}</span>
            </span>
            ${status}
            <span class="row-action ${actionClass}">
                <i class="fa-solid ${actionIcon}" aria-hidden="true"></i>
                ${this.#esc(actionLabel)}
            </span>
        `;

        row.addEventListener('click', () => {
            if (existing) {
                this.emit('page-edit', {pagePath: existing.path, templateName: tmpl.name, live: existing.live !== false, pending: existing.pending === true});
            } else {
                this.emit('template-select', {templateName: tmpl.name});
            }
        });

        return row;
    }

    /**
     * @param {import('../../shell/types/user.types.js').TemplateSummary} tmpl
     * @param {{path: string, published: boolean, live?: boolean, pending?: boolean} | null | undefined} existing
     * @param {boolean} isFragment
     * @returns {string}
     */
    #status(tmpl, existing, isFragment) {
        if (existing) {
            const url = isFragment ? '' : `<span class="status-url">${this.#esc(pageUrlPath(existing.path))}</span>`;
            return existing.published
                ? `<span class="row-status status-live"><span class="status-dot"></span>${this.#esc(t('templatePicker.statusLive'))}${url}</span>`
                : `<span class="row-status status-draft"><span class="status-dot"></span>${this.#esc(t('templatePicker.statusDraft'))}${url}</span>`;
        }
        if (typeof tmpl.pageCount !== 'number') return '';
        if (tmpl.singleInstance) {
            return `<span class="row-status status-missing">${this.#esc(t('templatePicker.statusNotCreated'))}</span>`;
        }
        const countKey = isFragment ? 'templatePicker.fragmentCount' : 'templatePicker.pageCount';
        return `<span class="row-status status-count">${this.#esc(tp(countKey, tmpl.pageCount))}</span>`;
    }

    #esc(str) {
        if (!str) return '';
        return str.replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#039;');
    }
}

customElements.define('template-picker', TemplatePickerComponent);
